/**
 * Soll der Ton eines Videos gerade hoerbar sein?
 *
 * HINTERGRUND (Jans Frage 04.08.2026: "es gibt auch Bilder, die ueber mehrere
 * Slides laufen und zum Teil auch ausgeblendet sind. Wenn ich das durch ein
 * Video ersetze, wird dann die ganze Zeit das Audio im Hintergrund
 * abgespielt?"):
 *
 * Ja, wurde es. Die Wiedergabe hing bis dahin nur an der Lebenszeit der Ebene
 * und — sofern `videoLiveUpdate` gesetzt war — an Blickfeld und Bildschirm.
 * Die DECKKRAFT spielte nirgends eine Rolle. Ein Video, das ueber fuenf Slides
 * laeuft und auf dreien ausgeblendet ist, war die ganze Zeit zu hoeren.
 *
 * REGEL: hoerbar nur, wenn die Ebene tatsaechlich zu sehen ist — also nicht
 * ausgeblendet UND im Bild. Stummgeschaltete Videos bleiben stumm; die
 * Entscheidung des Nutzers gewinnt immer.
 *
 * Bewusst nur STUMMSCHALTEN, nicht pausieren (Jans Wahl): das Video laeuft
 * unhoerbar weiter, steht beim Wiedereinblenden an der richtigen Stelle und
 * springt nicht. Lautstaerke und Ein-/Ausblendungen bleiben unberuehrt —
 * `muted` liegt quer dazu.
 */
export interface HoerbarkeitArgs {
    /** Vom Nutzer gesetztes Stummschalten (content.muted). Gewinnt immer. */
    contentMuted?: boolean;
    /** Wirksame Deckkraft inklusive uebergeordneter Gruppen (0..1). */
    opacity?: number;
    /** Liegt die Ebene im sichtbaren Bereich der Vorschau? */
    onScreen?: boolean;
    /** Liegt die Ebene im Blickfeld der Kamera? */
    inFrustum?: boolean;
    /**
     * Hat der Nutzer die Wiedergabe gestartet (START-Klick)? Jans Regel
     * 24.08.2026: Vor dem Start läuft ein Cover-Video zwar sichtbar weiter
     * (Benes Entscheidung 12.08. — ein nie gespieltes <video> liefert WebGL
     * nur Schwarz), darf aber NIE hörbar sein. Im Reader kaschierte das
     * bisher nur die Browser-Autoplay-Richtlinie (keine Geste seit
     * Seitenladung); in der Editor-Vorschau gab es die Geste (Vorschau-
     * Klick) und der Ton lief vor START. `undefined` heißt „nicht
     * angebunden" (z. B. Editor-Leinwand) — dann entscheiden die anderen
     * Regeln.
     */
    playerStarted?: boolean;
    /**
     * Ist dieses Video das EINE hörbare (Jans Regel 24.08.2026: höchstens
     * ein Video tönt — das zentralste, bei Gleichstand das vorderste)?
     * `false` → stumm; `undefined` = Auswahl nicht angebunden (Editor).
     */
    einzigesTonVideo?: boolean;
}

/**
 * Unterhalb dieser Deckkraft gilt eine Ebene als ausgeblendet. Nicht exakt 0,
 * weil Ein-/Ausblendungen als Fliesskomma laufen und am Ende einer Blende
 * Werte wie 0.0001 stehen — dort soll es bereits still sein.
 */
export const UNHOERBAR_AB = 0.02;

export function shouldVideoBeAudible(a: HoerbarkeitArgs): boolean {
    if (a.contentMuted) return false;
    if (a.playerStarted === false) return false;
    if (a.einzigesTonVideo === false) return false;
    const deckkraft = typeof a.opacity === "number" && Number.isFinite(a.opacity) ? a.opacity : 1;
    if (deckkraft <= UNHOERBAR_AB) return false;
    // `undefined` heisst "nicht gemessen" — dann nicht stummschalten, sonst
    // waeren Videos ohne Bildschirm-Pruefung faelschlich still.
    if (a.onScreen === false) return false;
    if (a.inFrustum === false) return false;
    return true;
}
